import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useGetImageryFavorites } from './useGetImageryFavorites';
import { selectImagery, selectImageryEnabled, selectSelectedImage, setImagery, setImageryEnabled, setSelectedImage } from '@state-mgmt/slices/visitSlice';
import { AppDispatch } from '@state-mgmt/store';
import { imageExportSubscriptionPng } from '@services/imagery';

interface ExportedPngMap {
  [id: string]: { url: string; bbox: number[][] } | null;
}
export default function useImagery({
  trialId,
  fieldId,
  protocolId,
  clientId
}: {
  trialId: string | null | undefined;
  fieldId: string | null | undefined;
  protocolId: string | null | undefined;
  clientId: string | null | undefined;
}) {
  const dispatch = useDispatch<AppDispatch>();
  const [, getImageryFavorites] = useGetImageryFavorites();

  const selectedImageId = useSelector(selectSelectedImage);
  const imagery = useSelector(selectImagery);
  const imageryEnabled = useSelector(selectImageryEnabled);

  const [exportedImagePngMap, setExportedImagePngMap] = useState<ExportedPngMap>({});

  useEffect(() => {
    setExportedImagePngMap({});
    if (fieldId && clientId && trialId && protocolId) {
      dispatch(setImageryEnabled(false));
      dispatch(setSelectedImage(null));
      dispatch(setImagery(null));
      let current = true;
      getImageryFavorites({ field_id: fieldId, client_id: clientId, trial_id: trialId, protocol_id: protocolId }).then(imagery => {
        if (!current) return;
        if (imagery?.status?.length) {
          const sorted = imagery.status.sort((a, b) => {
            const aDate = new Date(a.date);
            const bDate = new Date(b.date);
            return aDate.getTime() - bDate.getTime();
          });
          dispatch(setImagery(sorted));
          dispatch(setSelectedImage(sorted[0].id || null));
        } else {
          dispatch(setImagery(null));
          dispatch(setSelectedImage(null));
        }
      });
      return () => {
        current = false;
      };
    } else {
      dispatch(setSelectedImage(null));
      dispatch(setImagery(null));
      dispatch(setImageryEnabled(false));
    }
  }, [fieldId, protocolId, trialId, clientId, getImageryFavorites, dispatch]);

  const selectedImageStatus = useMemo(() => {
    return imageryEnabled && selectedImageId ? imagery?.find(i => i.id === selectedImageId) || null : null;
  }, [selectedImageId, imagery, imageryEnabled]);

  useEffect(() => {
    if (!selectedImageStatus?.id) return;
    if (exportedImagePngMap[selectedImageStatus.id]) return;
    imageExportSubscriptionPng({ id: selectedImageStatus.id })
      .then(response => {
        setExportedImagePngMap(prev => {
          return {
            ...prev,
            [selectedImageStatus.id || '']: response
          };
        });
      })
      .catch(error => {
        console.error('An error occurred fetching imagery png', error);
      });
  }, [selectedImageStatus]);

  const selectedImage: { url: string; bbox: number[][] } | null = useMemo(() => {
    if (!imageryEnabled) {
      return null;
    } else if (selectedImageStatus?.id && exportedImagePngMap[selectedImageStatus.id]) {
      return exportedImagePngMap[selectedImageStatus.id];
    } else if (selectedImageStatus?.ndre_imagery) {
      return {
        url: selectedImageStatus.ndre_imagery,
        bbox: selectedImageStatus.bbox
      };
    } else {
      return null;
    }
  }, [selectedImageStatus, exportedImagePngMap, imageryEnabled]);

  return {
    selectedImage
  };
}
